import { Box } from '@chakra-ui/react';
import { useState, useEffect, useCallback, createContext, useContext } from 'react';
import type { ReactNode } from 'react';

interface AriaLiveContextType {
  announce: (message: string, politeness?: 'polite' | 'assertive') => void;
}

const AriaLiveContext = createContext<AriaLiveContextType>({
  announce: () => undefined,
});

export function useAriaLive() {
  return useContext(AriaLiveContext);
}

export function AriaLiveProvider({ children }: { children: ReactNode }) {
  const [politeMessage, setPoliteMessage] = useState('');
  const [assertiveMessage, setAssertiveMessage] = useState('');

  const announce = useCallback((message: string, politeness: 'polite' | 'assertive' = 'polite') => {
    if (politeness === 'assertive') {
      setAssertiveMessage('');
      // Force re-render with new message
      requestAnimationFrame(() => setAssertiveMessage(message));
    } else {
      setPoliteMessage('');
      requestAnimationFrame(() => setPoliteMessage(message));
    }
  }, []);

  // Clear messages after announcement
  useEffect(() => {
    if (politeMessage) {
      const timer = setTimeout(() => setPoliteMessage(''), 3000);
      return () => clearTimeout(timer);
    }
  }, [politeMessage]);

  useEffect(() => {
    if (assertiveMessage) {
      const timer = setTimeout(() => setAssertiveMessage(''), 3000);
      return () => clearTimeout(timer);
    }
  }, [assertiveMessage]);

  const srOnlyStyle = {
    position: 'absolute' as const,
    width: '1px',
    height: '1px',
    padding: 0,
    margin: '-1px',
    overflow: 'hidden',
    clip: 'rect(0, 0, 0, 0)',
    whiteSpace: 'nowrap' as const,
    borderWidth: 0,
  };

  return (
    <AriaLiveContext.Provider value={{ announce }}>
      {children}
      <Box aria-live="polite" aria-atomic="true" role="status" style={srOnlyStyle}>
        {politeMessage}
      </Box>
      <Box aria-live="assertive" aria-atomic="true" role="alert" style={srOnlyStyle}>
        {assertiveMessage}
      </Box>
    </AriaLiveContext.Provider>
  );
}
